import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { HeartIcon, MarkGithubIcon } from '@primer/octicons-react';

// Custom color palette for the dark blue footer
const darkBlue = {
  bg: '#0a1929',           // deep, dark blue background
  bgLighter: '#112233',     // subtle gradient variant
  textPrimary: '#ffffff',   // primary text (white)
  textSecondary: '#e0e0e0', // secondary text (light gray)
  textTertiary: '#b0b0b0',  // tertiary/copyright text
  border: 'rgba(255, 255, 255, 0.1)',
  accent: '#3b82f6',        // bright blue for hover states
  socialBg: '#1e2a3a',      // background for social icons
};

const FooterContainer = styled.footer`
  background: ${darkBlue.bg};
  background: linear-gradient(135deg, ${darkBlue.bg} 0%, ${darkBlue.bgLighter} 100%);
  border-top: 1px solid ${darkBlue.border};
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.2);
  padding: 3rem 0 1.5rem;
  margin-top: auto;

  @media (max-width: 768px) {
    padding: 2rem 0 1rem;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
    gap: 1.5rem;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ColumnTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${darkBlue.textPrimary};
  margin: 0 0 0.5rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${darkBlue.border};
`;

const FooterLink = styled(Link)`
  font-size: 0.875rem;
  color: ${darkBlue.textSecondary};
  text-decoration: none;
  transition: color 0.2s ease;
  line-height: 1.6;

  &:hover {
    color: ${darkBlue.accent};
  }

  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const ExternalLink = styled.a`
  font-size: 0.875rem;
  color: ${darkBlue.textSecondary};
  text-decoration: none;
  transition: color 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: ${darkBlue.accent};
  }

  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${darkBlue.socialBg};
    color: ${darkBlue.textSecondary};
    transition: all 0.2s ease;
    border: 1px solid ${darkBlue.border};

    &:hover {
      background: ${darkBlue.accent};
      color: ${darkBlue.textPrimary};
      transform: translateY(-2px);
      border-color: ${darkBlue.accent};
    }

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const Copyright = styled.div`
  font-size: 0.8125rem;
  color: ${darkBlue.textTertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding-top: 1.5rem;
  border-top: 1px solid ${darkBlue.border};
  text-align: center;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding-top: 1rem;
    gap: 0.25rem;
  }
`;

const Heart = styled(HeartIcon)`
  color: #ef4444; /* keeps the red heart */
  margin: 0 0.125rem;
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const toolCategories = [
    {
      title: 'Core Tools',
      tools: [
        { name: 'JSON Validator', path: '/validator' },
        { name: 'JSON Viewer', path: '/viewer' },
        { name: 'JSON Converter', path: '/converter' },
        { name: 'JSON Query', path: '/query' },
        { name: 'JSON Compare', path: '/compare' },
      ],
    },
    {
      title: 'Advanced Tools',
      tools: [
        { name: 'JSON Schema', path: '/schema' },
        { name: 'Schema Validator', path: '/schema-validator' },
        { name: 'JSON Inspector', path: '/inspector' },
        { name: 'JSON Score', path: '/json-score' },
        { name: 'Encode / Decode', path: '/encoder' },
        { name: 'Code Generator', path: '/code-generator' },
      ],
    },
    {
      title: 'Security & Masking',
      tools: [
        { name: 'Security Scanner', path: '/security-scanner' },
        { name: 'JSON Masker', path: '/masker' },
      ],
    },
    {
      title: 'Visualization',
      tools: [{ name: 'Tree Visualizer', path: '/visualizer' }],
    },
    {
      title: 'Developer Tools',
      tools: [
        { name: 'API Tester', path: '/api-tester' },
        { name: 'Fake REST API', path: '/fake-api' },
        { name: 'Data Generator', path: '/generator' },
        { name: 'JSON Hosting', path: '/hosting' },
      ],
    },
    {
      title: 'Resources',
      tools: [
        { name: 'Learn JSON', path: '/learn' },
        { name: 'Documentation', path: '/documentation' },
        { name: 'About', path: '/about' },
        { name: 'Contact', path: '/contact' },
        { name: 'Feedback', path: '/feedback' },
      ],
    },
  ];

  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          {toolCategories.map((category) => (
            <FooterColumn key={category.title}>
              <ColumnTitle>{category.title}</ColumnTitle>
              {category.tools.map((tool) => (
                <FooterLink key={tool.path} to={tool.path}>
                  {tool.name}
                </FooterLink>
              ))}
            </FooterColumn>
          ))}

          <FooterColumn>
            <ColumnTitle>Legal</ColumnTitle>
            <FooterLink to="/privacy">Privacy Policy</FooterLink>
            <FooterLink to="/terms">Terms of Service</FooterLink>
            <FooterLink to="/cookies">Cookie Policy</FooterLink>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Connect</ColumnTitle>
            <ExternalLink
              href="https://github.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <MarkGithubIcon size={16} />
              GitHub
            </ExternalLink>
            {/* Social icons can be added here if desired */}
          </FooterColumn>
        </FooterGrid>

        <Copyright>
          <span>© {currentYear} JSON Tools. All rights reserved.</span>
          <span>•</span>
          <span>
            Made with <Heart size={12} /> by developers for developers
          </span>
        </Copyright>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;